import React, { useEffect, useState } from "react";
import { Input, Table } from "reactstrap";
import { useCommonData } from "../../../../../providers/commonDataProvider";
import { utils } from "../../../../../utils/utils";
import {
  useEvaOverride,
  ACTIONS as EVA_OVERRIDE_ACTIONS,
} from "../../../../../providers/evaOverrideProvider";
import { useJobDetails } from "../../../../../providers/jobDetailsProvider";
import { useQuoteItems } from "../../../../../providers/quoteItemsProvider";

const EstimatedActualEquipmentRentals = () => {
  const [evaOverrideContext, setEvaOverrideContext] = useEvaOverride();
  const [commonDataContext] = useCommonData();
  const [jobDetails] = useJobDetails();
  const [quoteItemsContext] = useQuoteItems();
  const [equipmentRentals, setEquipmentRentals] = useState([]);

  const [estimatedTotalCost, setEstimatedTotalCost] = useState(0);
  const [actualTotalCost, setActualTotalCost] = useState(0);
  const [differenceTotal, setDifferenceTotal] = useState(0);

  useEffect(() => {
    const quoteItemEquipmentRentals = utils.reduceQuoteItemsEquipmentRentals(
      quoteItemsContext.quoteItems
    );

    const equipmentRentals = commonDataContext.quoteItemEquipmentRentals.map(
      (quoteItemEquipmentRental) => {
        // Get actual cost from teklaMaterialData (similar to subcontractorRoles)
        const actualCost = jobDetails.job.teklaMaterialData
          .filter(
            (teklaMaterial) =>
              teklaMaterial.equipmentRentalId === quoteItemEquipmentRental.id
          )
          .reduce((p, c) => p + (c?.cost || 0), 0);

        const estimatedItem = quoteItemEquipmentRentals.find(
          (item) => item.equipmentRentalId === quoteItemEquipmentRental.id
        );

        const estimatedCost = estimatedItem?.cost || 0;

        return {
          ...quoteItemEquipmentRental,
          actualCost: actualCost,
          estimatedCost: estimatedCost,
        };
      }
    );

    if (!evaOverrideContext.editEnabled) {
      jobDetails.job.equipmentRentals = equipmentRentals;
    }
    setEquipmentRentals(equipmentRentals);
  }, [
    commonDataContext.quoteItemEquipmentRentals,
    quoteItemsContext,
    jobDetails.job,
    evaOverrideContext.editEnabled,
  ]);

  useEffect(() => {
    const { totalCost, totalActual } = equipmentRentals.reduce(
      (acc, equipmentRental) => {
        const actualCostOverride =
          evaOverrideContext.equipmentRentals[equipmentRental.id];

        return {
          totalCost: acc.totalCost + equipmentRental.estimatedCost,
          totalActual:
            acc.totalActual +
            (actualCostOverride || equipmentRental.actualCost),
        };
      },
      { totalCost: 0, totalActual: 0 }
    );

    const differenceTotal = totalCost - totalActual;

    if (!evaOverrideContext.editEnabled) {
      jobDetails.job.equipmentRentalsTotalActual = totalActual;
      jobDetails.job.equipmentRentalsTotalCost = totalCost;
      jobDetails.job.equipmentRentalsDifferenceTotal = differenceTotal;
      jobDetails.job.equipmentRentalsOverride =
        evaOverrideContext.equipmentRentals;
    }
    setActualTotalCost(totalActual);
    setEstimatedTotalCost(totalCost);
    setDifferenceTotal(differenceTotal);
  }, [
    equipmentRentals,
    evaOverrideContext.equipmentRentals,
    evaOverrideContext.editEnabled,
    jobDetails.job,
  ]);

  return (
    <Table className="col-12 px-0 border mt-4 rounded mb-0">
      <tbody>
        {equipmentRentals.map((equipmentRental, index) => {
          const actualCostOverride =
            evaOverrideContext.equipmentRentals[equipmentRental.id];
          const actualCost = actualCostOverride || equipmentRental.actualCost;

          return (
            <tr key={"quoteItemEquipmentRental" + index}>
              <td className="py-0 text-truncate" width={"45%"}>
                {equipmentRental.name}
              </td>
              <td className="py-0" width={"15%"}>
                <div readOnly className="form-control border text-right m-1">
                  {utils.formatCurrency(equipmentRental.estimatedCost)}
                </div>
              </td>
              <td className="py-0" width={"15%"}>
                <Input
                  className="form-control border text-right m-1 hide-arrows"
                  type={evaOverrideContext.editEnabled ? "number" : "text"}
                  step="0.1"
                  value={
                    evaOverrideContext.editEnabled
                      ? parseFloat(actualCost)
                      : utils.formatCurrency(actualCost)
                  }
                  readOnly={!evaOverrideContext.editEnabled}
                  onChange={(evt) =>
                    setEvaOverrideContext({
                      action: EVA_OVERRIDE_ACTIONS.UPDATE_ITEM,
                      payload: {
                        id: equipmentRental.id,
                        key: "equipmentRentals",
                        value: evt.currentTarget.value,
                      },
                    })
                  }
                />
              </td>
              <td className="py-0" width={"15%"}>
                <div
                  readOnly
                  className={`${
                    equipmentRental.estimatedCost - actualCost < 0
                      ? "text-danger"
                      : "text-success"
                  } form-control border text-right m-1 font-weight-bold`}
                >
                  {utils.formatCurrency(
                    equipmentRental.estimatedCost - actualCost
                  )}
                </div>
              </td>
            </tr>
          );
        })}
        <tr className="text-muted bg-lighter">
          <th width={"45%"} className="align-middle">
            Total
          </th>
          <td className="py-0" width={"15%"}>
            <div readOnly className="form-control border text-right m-1">
              {utils.formatCurrency(estimatedTotalCost)}
            </div>
          </td>
          <td className="py-0" width={"15%"}>
            <div readOnly className="form-control border text-right m-1">
              {utils.formatCurrency(actualTotalCost)}
            </div>
          </td>
          <td className="py-0" width={"15%"}>
            <div
              readOnly
              className={`form-control border text-right m-1 font-weight-bold ${
                differenceTotal < 0 ? "text-danger" : "text-success"
              }`}
            >
              {utils.formatCurrency(differenceTotal)}
            </div>
          </td>
        </tr>
      </tbody>
    </Table>
  );
};

export default EstimatedActualEquipmentRentals;
